import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthResponse, LoginRequest, RegisterRequest, GoogleAuthRequest, User, Tenant } from '../types';
import { apiService } from '../services/api';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  tenant: Tenant | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  tenant: null,
  token: localStorage.getItem('auth_token'),
  loading: false,
  error: null,
};

// Async thunks
export const loginAsync = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await apiService.login(credentials);
      
      // Store in localStorage
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('auth_user', JSON.stringify(response.user));
      localStorage.setItem('auth_tenant', JSON.stringify(response.tenant));
      
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al iniciar sesión'
      );
    }
  }
);

export const registerAsync = createAsyncThunk(
  'auth/register',
  async (userData: RegisterRequest, { rejectWithValue }) => {
    try {
      const response = await apiService.register(userData);
      
      // Store in localStorage
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('auth_user', JSON.stringify(response.user));
      localStorage.setItem('auth_tenant', JSON.stringify(response.tenant));
      
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al registrar usuario'
      );
    }
  }
);

export const phoneVerifyAsync = createAsyncThunk(
  'auth/phoneVerify',
  async (
    payload: { phone: string; code: string; utmSource?: string; utmMedium?: string; utmCampaign?: string; couponCode?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiService.phoneVerify(payload);

      // Store in localStorage (same shape as login/register)
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('auth_user', JSON.stringify(response.user));
      localStorage.setItem('auth_tenant', JSON.stringify(response.tenant));

      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'No pudimos verificar el código. Intentá de nuevo.'
      );
    }
  }
);

export const googleAuthAsync = createAsyncThunk(
  'auth/google',
  async (data: GoogleAuthRequest, { rejectWithValue }) => {
    try {
      const response = await apiService.googleAuth(data);

      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('auth_user', JSON.stringify(response.user));
      localStorage.setItem('auth_tenant', JSON.stringify(response.tenant));

      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al autenticar con Google'
      );
    }
  }
);

export const validateTokenAsync = createAsyncThunk(
  'auth/validateToken',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No token found');
      }

      await apiService.validateToken(token);

      // Re-hydrate user + tenant from /api/auth/me so we always have the
      // freshest onboardingCompletedAt (the client-side cached version can
      // be stale right after the user finishes the wizard on another device).
      try {
        const me = await apiService.getAuthMe();
        localStorage.setItem('auth_user', JSON.stringify(me.user));
        localStorage.setItem('auth_tenant', JSON.stringify(me.tenant));
        return { token, user: me.user, tenant: me.tenant };
      } catch {
        // Fallback: stored cache (older backends without /auth/me).
        const userStr = localStorage.getItem('auth_user');
        const tenantStr = localStorage.getItem('auth_tenant');
        if (!userStr || !tenantStr) throw new Error('User or tenant data not found');
        const user = JSON.parse(userStr);
        const tenant = JSON.parse(tenantStr);
        return { token, user, tenant };
      }
    } catch (error: any) {
      // Clear invalid data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_tenant');

      return rejectWithValue('Token inválido o expirado');
    }
  }
);

/**
 * Re-fetch /api/auth/me to refresh the tenant (e.g. after completing onboarding
 * or uploading a new logo). Does not touch the token.
 */
export const refreshMeAsync = createAsyncThunk(
  'auth/refreshMe',
  async (_, { rejectWithValue }) => {
    try {
      const me = await apiService.getAuthMe();
      localStorage.setItem('auth_user', JSON.stringify(me.user));
      localStorage.setItem('auth_tenant', JSON.stringify(me.tenant));
      return { user: me.user, tenant: me.tenant };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'No se pudo refrescar la sesión');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.tenant = null;
      state.token = null;
      state.error = null;
      
      // Clear localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_tenant');
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem('auth_user', JSON.stringify(state.user));
      }
    },
    updateTenant: (state, action: PayloadAction<Partial<Tenant>>) => {
      if (state.tenant) {
        state.tenant = { ...state.tenant, ...action.payload };
        localStorage.setItem('auth_tenant', JSON.stringify(state.tenant));
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tenant = action.payload.tenant;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.tenant = null;
        state.token = null;
        state.error = action.payload as string;
      });

    // Register
    builder
      .addCase(registerAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tenant = action.payload.tenant;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(registerAsync.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.tenant = null;
        state.token = null;
        state.error = action.payload as string;
      });

    // Phone OTP (login or signup via WhatsApp)
    builder
      .addCase(phoneVerifyAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(phoneVerifyAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tenant = action.payload.tenant;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(phoneVerifyAsync.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.tenant = null;
        state.token = null;
        state.error = action.payload as string;
      });

    // Google auth (login or signup)
    builder
      .addCase(googleAuthAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(googleAuthAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tenant = action.payload.tenant;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(googleAuthAsync.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.tenant = null;
        state.token = null;
        state.error = action.payload as string;
      });

    // Validate token
    builder
      .addCase(validateTokenAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(validateTokenAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tenant = action.payload.tenant;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(validateTokenAsync.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.tenant = null;
        state.token = null;
        state.error = action.payload as string;
      });

    // Refresh /me (after onboarding, logo upload, etc.)
    builder
      .addCase(refreshMeAsync.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.tenant = action.payload.tenant;
      });
  },
});

export const { logout, clearError, updateUser, updateTenant } = authSlice.actions;

export default authSlice.reducer;